import { useId, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { FormDialog } from '@/components/composed/FormDialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { ApiError } from '@/lib/api/errors';
import type { Permission } from '@/lib/api/domains/permissions';
import {
  permissionCatalogQuery,
  permissionSetCompositionQuery,
} from '../api/permission-queries';
import { useReplaceSetPermissions } from '../api/permission-mutations';

/*
 * The permission-set composer (F03 R46, R49). Built for task 3.2 against the
 * task-3.1 tests in PermissionSetComposer.test.tsx:
 *
 *   - AC-200ea95b (R46) — WHEN an administrator composes a permission set, THE
 *     composer SHALL present every available permission GATHERED UNDER THE KIND
 *     OF RECORD it governs. The available permissions (the catalog, relayed
 *     from Identity) are grouped by `resource` (the record kind) — one
 *     `role="group"` per record kind, labelled by that record kind, with a
 *     checkbox per available permission beneath it — not two flat columns.
 *   - AC-cf93b725 (R49) — WHEN the composer is opened for an EXISTING set, THE
 *     composer SHALL show the permissions that set currently contains as
 *     ALREADY CHOSEN. Opening for a set reads that set's current composition
 *     (`permissionSetCompositionQuery`) and pre-selects exactly those permission
 *     ids; opening for a new set (no `setId`) starts with nothing chosen.
 *
 * On save the WHOLE selection is submitted as the replace the domain path
 * forwards to Identity (R49): `useReplaceSetPermissions` sends the full
 * membership, never a delta — Identity applies it as one act (AC-7d54eb56),
 * drops any permission not named (AC-c9d9d80a) and leaves the set as it was on
 * failure (AC-e9637517). An empty selection is a valid empty set (R51), not a
 * no-op. Mutate → invalidate, no optimistic update (the server is the authority
 * on what the set contains after the change).
 *
 * The available permissions and the current composition are relayed from
 * Identity unchanged (domain-svc internal/rbac); this composer SHOWS and
 * SUBMITS — it decides no authorization outcome. F03 records; F03b refuses, so
 * nothing here is a refusal of its own; the server's refusal, when one comes,
 * is surfaced where the operator works, one generic text regardless of cause.
 */

/** A record-kind group: its label and the permissions it governs. */
interface RecordKindGroup {
  /** The record kind's key (`resource`) — stable, used as the group's React key. */
  resource: string;
  /** The record kind's human-readable label, shown as the group heading. */
  label: string;
  /** The permissions governed by this record kind. */
  permissions: Permission[];
}

/*
 * Gather the catalog under the record kind each permission governs
 * (AC-200ea95b): one group per `resource`, in first-seen order, each carrying
 * the human-readable label (falling back to the resource key so a reader never
 * sees a bare key — the catalog screen does the same).
 */
function groupByRecordKind(permissions: Permission[]): RecordKindGroup[] {
  const groups = new Map<string, RecordKindGroup>();
  for (const permission of permissions) {
    const existing = groups.get(permission.resource);
    if (existing) {
      existing.permissions.push(permission);
    } else {
      groups.set(permission.resource, {
        resource: permission.resource,
        label: permission.resourceLabel ?? permission.resource,
        permissions: [permission],
      });
    }
  }
  return [...groups.values()];
}

export function PermissionSetComposer({
  open,
  onOpenChange,
  setId,
}: {
  /** Whether the composer is open. */
  open: boolean;
  /** Called to request opening/closing. */
  onOpenChange: (open: boolean) => void;
  /**
   * The set being composed, when opening the composer for an EXISTING set — its
   * current permissions pre-select (AC-cf93b725). Omitted when composing a new
   * set (nothing pre-selected). `| undefined` is explicit so a caller may pass
   * it conditionally under exactOptionalPropertyTypes.
   */
  setId?: string | undefined;
}): React.ReactElement | null {
  const groupHeadingPrefix = useId();

  // The available permissions (the catalog, relayed from Identity). Every one
  // is offered as a selectable choice (AC-200ea95b).
  const { data: catalog } = useQuery(permissionCatalogQuery());
  const permissions = useMemo(() => catalog ?? [], [catalog]);

  // The current composition of an EXISTING set (AC-cf93b725). Read only when
  // the composer is opened for a set; a new set (no `setId`) has none. `enabled`
  // keeps the query from firing for a new set, so nothing is pre-selected.
  const { data: composition } = useQuery({
    ...permissionSetCompositionQuery(setId ?? ''),
    enabled: open && setId !== undefined,
  });

  const replaceSetPermissions = useReplaceSetPermissions(setId ?? '');

  // The chosen permission ids. Pre-seeded from the existing set's current
  // membership once it loads (AC-cf93b725); a new set starts with nothing
  // chosen. Held once the composition is in hand so the operator's edits are
  // not overwritten by a later refetch; `undefined` means "not yet seeded".
  const [selected, setSelected] = useState<Set<string> | undefined>(undefined);

  // The server's refusal, surfaced where the operator works. The composer
  // decides no outcome (F03 records; F03b refuses); it shows the server's own
  // generic text and keeps the composer open so the save is never shown as
  // success.
  const [refusal, setRefusal] = useState<string | null>(null);

  // Whether the set was just saved EMPTY (R51). An empty set is a valid,
  // accepted state (AC-14c5e3e6); when it is saved the composer stays open and
  // says plainly that it adds nothing to what its holders' roles already allow
  // (AC-8e332b40, AC-ad1c6f62 — the composer half). This is a LABEL over
  // Identity data, not a refusal: the save succeeded.
  const [savedEmpty, setSavedEmpty] = useState(false);

  // Seed the selection the first time the composition is known (existing set),
  // or to empty for a new set. Done in render — not an effect — so the first
  // paint after the composition loads already shows the pre-selected boxes.
  let chosen = selected;
  if (chosen === undefined) {
    if (setId === undefined) {
      chosen = new Set<string>();
    } else if (composition) {
      chosen = new Set(composition.permissionIds);
    }
  }

  if (!open) return null;

  // Until an existing set's composition has loaded, there is nothing to seed
  // the pre-selection from; render the groups once we can show them chosen
  // correctly. (For a new set `chosen` is already the empty set.)
  const current = chosen ?? new Set<string>();

  const groups = groupByRecordKind(permissions);

  function toggle(permissionId: string, next: boolean): void {
    // Any edit after an empty save clears the "adds nothing" label — it
    // described the set as saved, not as it is being re-composed.
    setSavedEmpty(false);
    setSelected((prev) => {
      const base = new Set(prev ?? current);
      if (next) base.add(permissionId);
      else base.delete(permissionId);
      return base;
    });
  }

  function handleSubmit(): void {
    // Nothing to submit for a new set that was never given an id — the replace
    // targets an existing set. (A create flow supplies the id; the composer
    // submits the whole membership against it.)
    if (setId === undefined) {
      onOpenChange(false);
      return;
    }
    setRefusal(null);
    setSavedEmpty(false);
    // The WHOLE selection — never a delta (AC-c9d9d80a). An empty selection is
    // a valid empty set (R51), submitted as the empty membership Identity
    // accepts (AC-14c5e3e6), not a no-op.
    const permissionIds = [...current];
    const isEmptySave = permissionIds.length === 0;
    replaceSetPermissions.mutate(
      { permissionIds },
      {
        onSuccess: () => {
          if (isEmptySave) {
            // The empty save was accepted. Keep the composer open and say,
            // plainly, that it adds nothing (AC-8e332b40, AC-ad1c6f62). A label
            // over Identity's accepted empty set, never a refusal.
            setSavedEmpty(true);
            return;
          }
          // A set that grants something: close as before — nothing to label.
          setSelected(undefined);
          onOpenChange(false);
        },
        onError: (error) => {
          // Surface the server's refusal verbatim where the operator works and
          // keep the composer open — the save failed, so nothing is shown as
          // saved. One generic alert regardless of cause; the text is the
          // server's (AC-e9637517 leaves the set as it was).
          setRefusal(
            error instanceof ApiError
              ? error.message
              : 'That permission set could not be saved.',
          );
        },
      },
    );
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      // The composer only ever edits an EXISTING set's membership now (R46's
      // tpa half, AC-200ea95b): name/description are Identity-owned and
      // creating a set is out of scope (finding 9), so the dead new-set path is
      // removed from the route. Titled for edit mode so the operator sees they
      // are changing a set that already exists. The `setId === undefined`
      // branch below is kept only as a defensive no-op; it is unreachable from
      // the UI.
      title="Edit permission set"
      onSubmit={handleSubmit}
      submitLabel="Save"
      pending={replaceSetPermissions.isPending}
    >
      {/* The server's refusal, where the operator works. The text is the
          server's; the composer stays open so the save is never shown as
          success. */}
      {refusal ? (
        <Alert variant="destructive" role="alert">
          <AlertDescription>{refusal}</AlertDescription>
        </Alert>
      ) : null}

      {/* An empty set was saved and accepted (AC-14c5e3e6). Say plainly that it
          adds nothing to what its holders' roles already allow (AC-8e332b40,
          AC-ad1c6f62) — a label over Identity's accepted empty set, in the
          spec's restrained wording, never a refusal. */}
      {savedEmpty ? (
        <Alert variant="default" role="status">
          <AlertDescription>
            This set adds nothing to what its holders&rsquo; roles already
            allow.
          </AlertDescription>
        </Alert>
      ) : null}

      {/* Every available permission, GATHERED UNDER THE KIND OF RECORD it
          governs (AC-200ea95b): one group per record kind, labelled by that
          record kind, with a checkbox per permission beneath it. */}
      {groups.map((group) => {
        const headingId = `${groupHeadingPrefix}-${group.resource}`;
        return (
          <section
            key={group.resource}
            role="group"
            aria-labelledby={headingId}
            className="flex flex-col gap-2"
          >
            <h3 id={headingId} className="text-sm font-medium">
              {group.label}
            </h3>
            <ul className="flex flex-col gap-2">
              {group.permissions.map((permission) => {
                const checkboxId = `${groupHeadingPrefix}-${permission.id}`;
                return (
                  <li key={permission.id} className="flex items-center gap-2">
                    <Checkbox
                      id={checkboxId}
                      checked={current.has(permission.id)}
                      onCheckedChange={(next) =>
                        toggle(permission.id, next === true)
                      }
                    />
                    <label htmlFor={checkboxId} className="text-sm">
                      {permission.action}
                    </label>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </FormDialog>
  );
}
